import React, {useEffect} from 'react';
import {RouteProps} from 'react-router';
import {showToast} from 'common';
import {PortalSecureRoute, useCurrentUser} from 'security';
import {Access} from '../domain/accessMap';
import {useDestinationAccess} from '../domain/useAccessMap';
import {ProviderConditionalRedirect} from './ProviderConditionalRedirect';

const PROVIDER_TEAM = ['PROVIDER'] as const;

type PortalFeatureRouteProps = RouteProps & {
    requiredAccess?: Access;
    /** Access-map key. Set this instead of requiredAccess when the destination carries a runtime flag. */
    destination?: string;
};

const DENY_ALL: Access = () => false;

/**
 * A PROVIDER-role route with an added per-feature permission gate.
 * - role fails        → PortalSecureRoute's existing "Insufficient Permission" alert (unchanged).
 * - role OK, no token → toast + redirect to the user's first accessible page (or no-access URL).
 * - both OK           → render the route.
 *
 * Pass `destination` when the gate depends on a runtime feature flag. The gate is then resolved
 * here, by a hook, which is what lets it see the flags: this component renders below CurrentUser,
 * where the flag provider is mounted. Resolving it in the route list instead would read the empty
 * default map, and an empty map keeps every negated-flag destination, so the route would stay open
 * in every environment with nothing looking wrong.
 */
export const PortalFeatureRoute = ({requiredAccess, destination, ...routeProps}: PortalFeatureRouteProps) => {
    const currentUser = useCurrentUser();
    const flaggedAccess = useDestinationAccess(destination ?? '');
    const gate = destination ? flaggedAccess : (requiredAccess ?? DENY_ALL);
    const denied = !!currentUser && currentUser.role === 'PROVIDER' && !gate(currentUser);

    useEffect(() => {
        if (denied) {
            showToast.error('You do not have access to that page.');
        }
    }, [denied]);

    if (denied) {
        return <ProviderConditionalRedirect/>;
    }

    return <PortalSecureRoute allowedRoles={PROVIDER_TEAM} {...routeProps}/>;
};
